import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Mail, Check, X } from 'lucide-react';
import FloatingDrawer from './FloatingDrawer';

/**
 * ChatsDrawer - Premium messages drawer with search, friend requests, and staggered animations
 */
export default function ChatsDrawer({
  isOpen = false,
  onClose,
  chats = [],
  onChatSelect,
  onAddChat,
  loading = false,
  // Friend request props
  pendingRequests = [],
  onAcceptRequest,
  onRejectRequest,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredChats = chats.filter((chat) =>
    chat.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose} height="75vh">
      <div className="flex flex-col h-full bg-gjyl-dark">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h2 className="text-xl font-bold text-white">Messages</h2>
          <motion.button
            className="w-10 h-10 rounded-full bg-gjyl-accent flex items-center justify-center text-white"
            onClick={onAddChat}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            <Plus size={20} strokeWidth={2.5} />
          </motion.button>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-zinc-800 text-white text-sm rounded-full placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gjyl-accent/50 transition-all"
            />
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-2">
          {/* Friend Requests Section */}
          {pendingRequests.length > 0 && !searchQuery && (
            <div className="mb-4">
              <div className="flex items-center gap-2 px-3 py-2">
                <Mail className="w-4 h-4 text-gjyl-accent" />
                <span className="text-xs font-semibold text-gjyl-accent uppercase tracking-wider">
                  Friend Requests ({pendingRequests.length})
                </span>
              </div>
              <div className="space-y-1">
                {pendingRequests.map((request, index) => (
                  <FriendRequestItem
                    key={request.friendshipId || request.id}
                    request={request}
                    onAccept={() => onAcceptRequest?.(request.friendshipId, request.username)}
                    onReject={() => onRejectRequest?.(request.friendshipId)}
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Chat list */}
          {loading ? (
            <SkeletonLoader />
          ) : filteredChats.length === 0 && pendingRequests.length === 0 ? (
            <EmptyState searchQuery={searchQuery} onAddChat={onAddChat} />
          ) : filteredChats.length === 0 && searchQuery ? (
            <EmptyState searchQuery={searchQuery} onAddChat={onAddChat} />
          ) : (
            <div className="space-y-1 pb-2">
              {filteredChats.map((chat, index) => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  onClick={() => {
                    onChatSelect(chat.id);
                    onClose();
                  }}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </FloatingDrawer>
  );
}

/**
 * FriendRequestItem - Pending friend request with accept/reject buttons
 */
function FriendRequestItem({ request, onAccept, onReject, index }) {
  return (
    <motion.div
      className="flex items-center space-x-3 px-3 py-3 rounded-xl bg-gradient-to-r from-gjyl-accent/10 to-purple-500/10 border border-gjyl-accent/20"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      {/* Avatar */}
      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
        {request.avatar_url ? (
          <img
            src={request.avatar_url}
            alt={request.username}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
            {request.username?.charAt(0).toUpperCase() || '?'}
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-semibold text-sm truncate">
          {request.display_name || request.username || 'Unknown'}
        </h3>
        <p className="text-gray-400 text-xs">wants to be friends</p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-shrink-0">
        <motion.button
          className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white"
          onClick={(e) => {
            e.stopPropagation();
            onAccept?.();
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Check size={18} strokeWidth={3} />
        </motion.button>
        <motion.button
          className="w-9 h-9 rounded-full bg-red-500/80 flex items-center justify-center text-white"
          onClick={(e) => {
            e.stopPropagation();
            onReject?.();
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X size={18} strokeWidth={3} />
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * ChatItem - Individual chat list item with premium styling
 */
function ChatItem({ chat, onClick, index }) {
  return (
    <motion.div
      className="flex items-center space-x-3 px-3 py-3 rounded-xl hover:bg-white/5 cursor-pointer select-none transition-colors"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      {/* Avatar with status indicator */}
      <div className="relative flex-shrink-0">
        <div className="w-14 h-14 rounded-full overflow-hidden">
          {chat.avatar_url ? (
            <img
              src={chat.avatar_url}
              alt={chat.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${chat.gradient || 'from-purple-500 via-pink-500 to-orange-500'} flex items-center justify-center text-white font-bold text-lg`}>
              {chat.username?.charAt(0).toUpperCase() || '?'}
            </div>
          )}
        </div>

        {/* Status dot with black border */}
        <div
          className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-gjyl-dark ${
            chat.status === 'online' ? 'bg-green-500' : 'bg-gray-500'
          }`}
        />
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        {/* Top row: Username + Timestamp */}
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-bold text-sm truncate">
            {chat.username || 'Unknown User'}
          </h3>
          {chat.lastMessageTime && (
            <span className="text-gray-400 text-xs flex-shrink-0 ml-2">
              {formatTime(chat.lastMessageTime)}
            </span>
          )}
        </div>

        {/* Bottom row: Last message + Unread badge */}
        <div className="flex items-center justify-between">
          <p className="text-gray-400 text-sm truncate flex-1">
            {chat.status === 'typing' ? (
              <span className="italic text-gjyl-accent">typing...</span>
            ) : (
              chat.lastMessage || 'No messages yet'
            )}
          </p>
          {chat.unread > 0 && (
            <motion.div
              className="flex-shrink-0 ml-2 bg-gjyl-accent rounded-full min-w-[20px] h-5 flex items-center justify-center px-2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.05 + 0.2, type: 'spring' }}
            >
              <span className="text-xs font-bold text-white">
                {chat.unread > 99 ? '99+' : chat.unread}
              </span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * SkeletonLoader - Loading placeholder
 */
function SkeletonLoader() {
  return (
    <div className="space-y-3 py-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center space-x-3 px-3">
          {/* Avatar skeleton */}
          <motion.div
            className="w-14 h-14 rounded-full bg-zinc-800"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'easeInOut',
              delay: i * 0.1
            }}
          />

          {/* Text skeletons */}
          <div className="flex-1 space-y-2">
            <motion.div
              className="h-4 bg-zinc-800 rounded w-2/3"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut',
                delay: i * 0.1
              }}
            />
            <motion.div
              className="h-3 bg-zinc-800 rounded w-4/5"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut',
                delay: i * 0.1 + 0.2
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * EmptyState - No chats placeholder
 */
function EmptyState({ searchQuery, onAddChat }) {
  return (
    <motion.div
      className="flex flex-col items-center justify-center h-full text-center px-8 py-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="text-3xl"
        >
          💬
        </motion.div>
      </div>
      {searchQuery ? (
        <>
          <h3 className="text-white font-bold text-lg mb-2">No results</h3>
          <p className="text-gray-400 text-sm">
            No conversations match "{searchQuery}"
          </p>
        </>
      ) : (
        <>
          <h3 className="text-white font-bold text-lg mb-2">No messages yet</h3>
          <p className="text-gray-400 text-sm mb-6">
            Start a conversation with your friends
          </p>
          <motion.button
            className="px-6 py-2.5 bg-gjyl-accent text-white rounded-full font-semibold text-sm"
            onClick={onAddChat}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            New Chat
          </motion.button>
        </>
      )}
    </motion.div>
  );
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp) {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = now - time;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;

  return time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
